import { useMemo, useState } from 'react';
import api from './api';
import { extractBarkToken } from './bark';
import { useToast } from './Toast';
import './BarkGuide.css';

const GUIDE_STEPS = [
  {
    title: '打开 Bark App',
    description: '在 iPhone 桌面找到 Bark，如果还未安装请前往 App Store 搜索并下载，点击进入 Bark 应用。',
    imageUrl: 'https://xin-feng.xyz/bark-guide/open-bark.jpeg'
  },
  {
    title: '进入服务器',
    description: '点击底部“服务器”，再点击“服务器列表”。',
    imageUrl: 'https://xin-feng.xyz/bark-guide/open-servers.jpeg'
  },
  {
    title: '选择服务器',
    description: '在服务器列表中选择 api.day.app。',
    imageUrl: 'https://xin-feng.xyz/bark-guide/choose-server.jpeg'
  },
  {
    title: '复制地址和 Key',
    description: '点击“复制地址和 Key”，再把完整链接或 Key 粘贴到下方。',
    imageUrl: 'https://xin-feng.xyz/bark-guide/copy-address.jpeg'
  }
];

const isValidBarkToken = (token) => /^[A-Za-z0-9]+$/.test(token);

function BarkGuide({ onComplete, onLogout, embedded = false }) {
  const [tokenInput, setTokenInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { addToast } = useToast();

  const previewToken = useMemo(() => extractBarkToken(tokenInput), [tokenInput]);

  const handleSave = async () => {
    const processedToken = extractBarkToken(tokenInput);
    if (!processedToken) {
      addToast('请先粘贴 Bark 地址或 Token', 'error');
      return;
    }
    if (!isValidBarkToken(processedToken)) {
      addToast('Bark Token 格式不正确，只能包含数字或字母，请重新按照上文的教程指引进行操作', 'error');
      return;
    }

    setIsSaving(true);
    try {
      await api.post('/api/update-bark-token', {
        barkToken: processedToken,
        source: 'barkGuide'
      });
      addToast('Bark Token 已保存', 'success');
      onComplete();
    } catch (error) {
      addToast(error.response?.data?.error || '保存失败，请稍后重试', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const guideContent = (
    <div className="bark-guide-content">
      <section className="bark-guide-steps" aria-label="Bark Token 设置步骤">
        {GUIDE_STEPS.map((step, index) => (
          <article className="bark-step-card" key={step.title}>
            <div className="bark-step-index">0{index + 1}</div>
            <figure className="bark-guide-image-wrap">
              <img className="bark-guide-image" src={step.imageUrl} alt={`${step.title}示意图`} />
            </figure>
            <h2>{step.title}</h2>
            <p>{step.description}</p>
          </article>
        ))}
      </section>

      <section className="bark-guide-form">
        <label htmlFor="bark-token-input">粘贴 Bark 地址或 Token</label>
        <textarea
          id="bark-token-input"
          value={tokenInput}
          onChange={(event) => setTokenInput(event.target.value)}
          placeholder="例如：https://api.day.app/Ng8kVtP3xQmZr6YbLwS2Df/"
          rows={3}
        />
        <div className="bark-token-preview">
          <span>将保存为：</span>
          <strong>{previewToken || '等待粘贴'}</strong>
        </div>
        <button
          className="bark-guide-save"
          type="button"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? '保存中...' : '保存并进入系统'}
        </button>
      </section>
    </div>
  );

  if (embedded) {
    return guideContent;
  }

  return (
    <div className="bark-guide-page">
      <div className="bark-guide-shell">
        <header className="bark-guide-header">
          <div>
            <h1>设置 Bark Token</h1>
            <p>
              Bark Token 用于接收任务提醒。按示意图复制 Bark 的地址和 Key，粘贴后即可继续使用。
            </p>
          </div>
          <button className="bark-guide-logout" type="button" onClick={onLogout}>
            退出登录
          </button>
        </header>

        {guideContent}
      </div>
    </div>
  );
}

export default BarkGuide;
